import { clsx } from "clsx";
import type { FC } from "react";
import { SymbolIcon, type TooltipProps } from "widgets";

import { IndexStatus, type ProjectFile } from "@/graphql.ts";
import type { BrowsingProjectFileFragment } from "@/modules/files/graphql/files-queries.gql.ts";
import { getHumanReadableIndexingStatus, isBamFile } from "@/modules/files/utils/file.ts";

import { TooltipController } from "../../../overlays/components/OverlayControllers.js";
import style from "../../styles/Cells.module.scss";

export interface IndexStatusIconProps {
  file: ProjectFile | BrowsingProjectFileFragment;
  newIndexingStatus: IndexStatus | null;
  tooltip?: TooltipProps["content"];
}

const FileIndexStatusIcon: FC<IndexStatusIconProps> = (props) => {
  // do not show the indexing status if it is okay and was not changed during the user session
  const indexingStatus =
    props.newIndexingStatus || (props.file.indexingStatus === IndexStatus.Exists ? null : props.file.indexingStatus);
  const isBamFileUnindexed: boolean = isBamFile(props.file) && !!indexingStatus;
  const tooltip = getHumanReadableIndexingStatus(indexingStatus);

  return (
    isBamFileUnindexed && (
      <TooltipController content={tooltip}>
        {(tooltipTriggerProps) =>
          indexingStatus === IndexStatus.Exists ? (
            <SymbolIcon
              icon={"check_circle"}
              className={clsx(style.cell_file_tag, style.cell_file_tag_ok)}
              domProps={tooltipTriggerProps}
              filled
            />
          ) : indexingStatus === IndexStatus.Indexing ? (
            <SymbolIcon
              // @ts-expect-error Legacy Icon. Type mismatch
              icon={"auto_mode"}
              className={clsx(style.cell_file_tag, style.cell_file_tag_warning)}
              domProps={tooltipTriggerProps}
            />
          ) : indexingStatus === IndexStatus.Missing ? (
            <SymbolIcon
              icon={"cancel"}
              className={clsx(style.cell_file_tag, style.cell_file_tag_error)}
              domProps={tooltipTriggerProps}
              filled
            />
          ) : indexingStatus === IndexStatus.Failed || indexingStatus === IndexStatus.Cancelled ? (
            <SymbolIcon
              icon={"error"}
              className={clsx(style.cell_file_tag, style.cell_file_tag_error)}
              domProps={tooltipTriggerProps}
            />
          ) : null
        }
      </TooltipController>
    )
  );
};

export { FileIndexStatusIcon };
